import type { ShapeItem } from "@/components/maps/map-panel/map-panel-details-panel/components/shape-item.types";
import type { IStudentListPreviewRequest } from "@/providers/store/api/reports/reports.interface";

export interface IStudentListPreviewSessionPayload {
  request: IStudentListPreviewRequest;
  title?: string;
}

const STUDENT_LIST_BASE_TITLE = "Student List";
const STUDENT_LIST_PREVIEW_FALLBACK = "Student List Preview";

/**
 * Builds the report title shown in the workspace tab and document header,
 * matching Selected Area shape display names (`ShapeItem.value`).
 */
export function buildStudentListReportTitle(shapes: ShapeItem[]): string {
  const areaNames = shapes.map((shape) => shape.value.trim()).filter((name) => name.length > 0);

  if (areaNames.length === 0) {
    return STUDENT_LIST_PREVIEW_FALLBACK;
  }

  return `${STUDENT_LIST_BASE_TITLE} (${areaNames.join(", ")})`;
}

export function parseStudentListPreviewSession(
  storedPayload: string,
): { request: IStudentListPreviewRequest; title: string } | null {
  try {
    const parsed = JSON.parse(storedPayload) as
      | IStudentListPreviewSessionPayload
      | IStudentListPreviewRequest;

    if (parsed && typeof parsed === "object" && "request" in parsed) {
      return {
        request: parsed.request,
        title: parsed.title?.trim() ?? STUDENT_LIST_PREVIEW_FALLBACK,
      };
    }

    return {
      request: parsed,
      title: STUDENT_LIST_PREVIEW_FALLBACK,
    };
  } catch {
    return null;
  }
}

/** Area label inside `Student List (…)` report titles, if present. */
export function extractAreaNameFromStudentListTitle(title: string): string | null {
  const prefix = `${STUDENT_LIST_BASE_TITLE} (`;
  if (!title.startsWith(prefix) || !title.endsWith(")")) {
    return null;
  }

  const areaName = title.slice(prefix.length, -1).trim();
  return areaName.length > 0 ? areaName : null;
}

export function buildStudentListSummarySectionTitle(
  summaryMode: "school" | "grade",
  reportTitle: string,
): string {
  const dimension = summaryMode === "school" ? "School" : "Grade";
  const areaName = extractAreaNameFromStudentListTitle(reportTitle);

  if (!areaName) {
    return `Students by ${dimension}`;
  }

  return `Students by ${dimension} in ${areaName}`;
}
